import { createRoot } from 'react-dom/client';
import { useEffect, useState } from 'react';
import { Link, api, fmtCredits, navigate, useApi, usePath } from './lib.jsx';
import { CreditMeter, Icon, Wordmark } from './ui.jsx';
import { SetPassword, SignIn, Authorize } from './pages/SignIn.jsx';
import { Demo, SampleReport } from './pages/Demo.jsx';
import { PrintReport } from './pages/Print.jsx';
import { Dashboard, Reports } from './pages/Home.jsx';
import { Account } from './pages/Account.jsx';
import { McpPage } from './pages/Mcp.jsx';
import { NewResearch } from './pages/NewResearch.jsx';
import { RunPage } from './pages/Run.jsx';
import { Welcome } from './pages/Welcome.jsx';

const initials = (s) => s.split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');

/** The credits box at the foot of the navigation; the account page has the detail. */
function SideCredits({ c }) {
  return (
    <Link to="/account" className="side-credits" aria-label={`${c.team ? `${fmtCredits(c.used)} credits used` : `${fmtCredits(c.left)} credits remaining`}. Open your account`}>
      {c.team ? <>
        <span className="label">Credits used</span>
        <span className="big">{fmtCredits(c.used)}</span>
        <span className="small muted">Team account: research is not limited by credits.</span>
      </> : <>
        <span className="label">Credits remaining</span>
        <span className="big">{fmtCredits(c.left)} <small>of {fmtCredits(c.total)}</small></span>
        <CreditMeter used={c.used} total={c.total} />
        <span className="small muted">{fmtCredits(c.used)} used so far</span>
      </>}
    </Link>
  );
}

function Sidebar({ route, user, credits, open, onClose, onSignOut }) {
  const here = (test) => (test ? 'page' : undefined);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  return (
    <>
      {open ? <div className="side-backdrop" onClick={onClose} /> : null}
      <aside className={`sidebar${open ? ' open' : ''}`}>
        <div className="side-top">
          <Link to="/" className="wordmark" aria-label="Yapscape home"><Wordmark /></Link>
          <button type="button" className="icon-btn hide-wide" onClick={onClose} aria-label="Close menu"><Icon name="close" /></button>
        </div>
        <Link to="/new" className="btn primary side-new"><Icon name="plus" />New research</Link>
        <nav className="side-nav" aria-label="Main">
          <Link to="/" aria-current={here(route === '/')}><Icon name="home" />Home</Link>
          <Link to="/reports" aria-current={here(route === '/reports' || route.startsWith('/runs/'))}><Icon name="reports" />Reports</Link>
          <Link to="/sample" aria-current={here(route === '/sample')}><Icon name="sample" />Sample report</Link>
          <Link to="/mcp" aria-current={here(route === '/mcp')}><Icon name="plug" />MCP</Link>
          <Link to="/account" aria-current={here(route === '/account')}><Icon name="account" />Account</Link>
        </nav>
        <div className="side-foot">
          {credits ? <SideCredits c={credits} /> : null}
          <div className="side-user">
            <span className="avatar" aria-hidden="true">{initials(user.name || user.email)}</span>
            <span className="who"><b>{user.name || user.email}</b><span>{user.workspace.name}</span></span>
            <button type="button" className="icon-btn" onClick={onSignOut} aria-label="Sign out" title="Sign out"><Icon name="out" /></button>
          </div>
        </div>
      </aside>
    </>
  );
}

function App() {
  const path = usePath();
  // Read again on every page change, so the credits shown stay current.
  const me = useApi('/api/me', [path]);
  const [welcomed, setWelcomed] = useState(false);
  const [menu, setMenu] = useState(false);
  useEffect(() => setMenu(false), [path]);
  const user = me.data?.user;
  const credits = me.data?.credits;
  const cfg = useApi(user ? '/api/config' : null, [user?.id]);

  const route = path.split('?')[0];
  const version = new URLSearchParams(path.split('?')[1] ?? '').get('version');
  // index.html's title is the sign-in page's (what search engines show); the sample report and signed-in pages name themselves.
  useEffect(() => { document.title = route.startsWith('/demo') ? 'Sample report | Yapscape' : user ? 'Yapscape' : 'Sign in | Yapscape'; }, [route, user?.id]);
  // Public pages: no sign-in needed, saved data only.
  if (route === '/demo') return <Demo />;
  if (route === '/demo/print') return <PrintReport base="/api/demo" />;
  if (me.loading) return null;
  if (route === '/set-password') return <SetPassword onDone={() => { me.reload(); navigate('/', { replace: true }); }} />;
  if (!user) return <SignIn onDone={me.reload} />;
  if (route === '/oauth/authorize') return <Authorize user={user} />;
  if (!user.onboarded && !welcomed) return <Welcome user={user} credits={credits} onDone={() => setWelcomed(true)} />;
  const printMatch = route.match(/^\/reports\/([a-f0-9]+)\/print$/);
  if (printMatch) return <PrintReport base={`/api/runs/${printMatch[1]}`} version={version} />;

  const runMatch = route.match(/^\/runs\/([a-f0-9]+)$/);
  let page;
  if (route === '/new') page = cfg.data ? <NewResearch cfg={cfg.data} /> : null;
  else if (runMatch) page = <RunPage key={runMatch[1]} id={runMatch[1]} me={user} />;
  else if (route === '/reports') page = <Reports />;
  else if (route === '/sample') page = <SampleReport />;
  else if (route === '/account') page = <Account user={user} credits={credits} />;
  else if (route === '/mcp') page = <McpPage credits={credits} />;
  else page = <Dashboard user={user} credits={credits} />;

  async function signOut() { await api('/api/auth/logout', { method: 'POST', body: {} }); navigate('/', { replace: true }); me.reload(); }
  return (
    <div className="app">
      <Sidebar route={route} user={user} credits={credits} open={menu} onClose={() => setMenu(false)} onSignOut={signOut} />
      <div className="app-main">
        <header className="mobilebar">
          <button type="button" className="icon-btn" onClick={() => setMenu(true)} aria-label="Open menu"><Icon name="menu" size={20} /></button>
          <Link to="/" className="wordmark" aria-label="Yapscape home"><Wordmark /></Link>
          {credits && !credits.team ? <Link to="/account" className="credits-pill">{fmtCredits(credits.left)} credits</Link> : null}
        </header>
        <main>{page}</main>
      </div>
    </div>
  );
}

createRoot(document.getElementById('root')).render(<App />);
